import { createFileRoute, redirect } from "@tanstack/react-router"
import { useEffect } from "react"

import { listPendingInvites } from "@/http/invites"
import { logger } from "@/lib/logger"
import { AuthHome } from "@/pages/(auth)/home/page"
import { useOrganization } from "@/store/organization"

export const Route = createFileRoute("/_authenticated/home")({
  beforeLoad: async () => {
    // Check if we need to redirect based on organization count
    const { organizations, loadOrganizations } = useOrganization.getState()

    // Load organizations if not already loaded
    if (organizations.length === 0) {
      logger.info("Loading organizations for home redirect logic")
      await loadOrganizations()
    }

    const { organizations: loadedOrgs } = useOrganization.getState()

    logger.info("Home route - checking organization redirect", {
      count: loadedOrgs.length,
    })

    // If user has organizations, redirect to appropriate location
    if (loadedOrgs.length === 1) {
      // Auto-select single organization
      const org = loadedOrgs[0]
      logger.info("Single organization - redirecting from home", {
        slug: org.slug,
      })
      throw redirect({
        to: "/$orgSlug/dashboard",
        params: { orgSlug: org.slug },
      })
    }

    if (loadedOrgs.length > 1) {
      // Multiple organizations - show selection screen
      logger.info("Multiple organizations - redirecting to selection from home")
      throw redirect({ to: "/select-organization" })
    }

    if (loadedOrgs.length === 0) {
      const pendingInvites = await listPendingInvites()
      if (pendingInvites.data?.invites?.length) {
        logger.info("Pending invites found - redirecting to invites")
        throw redirect({ to: "/invites" })
      }

      // No organizations - redirect to onboarding
      logger.info("No organizations - redirecting to onboarding from home")
      throw redirect({ to: "/onboarding" })
    }

    // If 0 organizations, stay on home (onboarding will be in Phase 3)
    return null
  },
  loader: () => {
    return {
      title: "",
      path: "/home",
    }
  },
  component: AuthenticatedAppHome,
})

function AuthenticatedAppHome() {
  const path = Route.useLoaderData()
  const { breadcrumbs } = Route.useRouteContext()

  useEffect(() => {
    breadcrumbs.setCurrentPath(path)
  }, [breadcrumbs, path])

  return <AuthHome />
}
